import { useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import {
  generateAIVideos,
  generateInterviewVideos,
  getInterviewVideoStatus,
  getVideoStatus,
} from '../../../services/Interviews/interviewService';

export default function VideoGeneration({ uuid, videoStatus, setVideoStatus, type = 'questions' }) {
  const [generating, setGenerating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const isQuestionMedia = type === 'questions';
  const rows = useMemo(() => Object.entries(videoStatus || {}), [videoStatus]);

  const handleUpdateStatus = async () => {
    setRefreshing(true);
    try {
      const result = isQuestionMedia
        ? await getVideoStatus({ params: { uuid } })
        : await getInterviewVideoStatus({ params: { uuid } });
      setVideoStatus(result);
    } catch (error) {
      toast.error('Media status could not be refreshed.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      if (isQuestionMedia) await generateAIVideos({ params: { uuid } });
      else await generateInterviewVideos({ params: { uuid } });
      toast.success('AI media generation has been queued.');
      await handleUpdateStatus();
    } catch (error) {
      toast.error('AI media generation could not be started.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <section aria-labelledby="ai-media-title" className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-start">
        <div>
          <div className="flex items-center gap-2">
            <SparklesIcon className="h-6 w-6 text-purple-600" />
            <h2 id="ai-media-title" className="text-lg font-bold">AI interviewer media</h2>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">Generate missing or failed presenter videos, then refresh to follow progress for each AI agent.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={handleUpdateStatus} disabled={refreshing} className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-700">
            <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing' : 'Refresh status'}
          </button>
          <button type="button" onClick={handleGenerate} disabled={generating} className="inline-flex items-center gap-2 rounded-xl bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-50">
            <SparklesIcon className="h-4 w-4" />
            {generating ? 'Queueing…' : 'Generate missing media'}
          </button>
        </div>
      </div>
      {rows.length ? (
        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {rows.map(([key, status]) => (
            <article key={key} className="rounded-xl bg-gray-50 p-4 dark:bg-gray-900">
              <h3 className="font-semibold">{status.agent_name}</h3>
              <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                <span className="flex items-center gap-1 text-green-700 dark:text-green-300"><CheckCircleIcon className="h-4 w-4" />{status.completed || 0} ready</span>
                <span className="flex items-center gap-1 text-amber-700 dark:text-amber-300"><ArrowPathIcon className="h-4 w-4" />{status.generating || 0} active</span>
                <span className="flex items-center gap-1 text-red-700 dark:text-red-300"><ExclamationTriangleIcon className="h-4 w-4" />{status.failed || 0} failed</span>
              </div>
            </article>
          ))}
        </div>
      ) : <p className="mt-4 rounded-xl bg-gray-50 p-4 text-sm text-gray-500 dark:bg-gray-900">No presenter media status is available yet.</p>}
    </section>
  );
}
